import React, { useState, useEffect } from 'react';
import { Mosque, MosqueFormData } from '../types/mosque';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { useModalAnimation } from '../hooks/useModalAnimation';
import SegmentedControl from './SegmentedControl';
import ButtonSpinner from './ButtonSpinner';
import { Globe, Lock, CheckCircle, X, AlertTriangle } from 'lucide-react';

interface SuggestEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    mosqueId: string,
    changes: Partial<MosqueFormData>,
    reason: string,
    submitterName?: string
  ) => Promise<void>;
  mosque: Mosque | null;
}

const COUNTDOWN_SECONDS = 3;
const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;
const LATITUDE_MIN = -90;
const LATITUDE_MAX = 90;
const LONGITUDE_MIN = -180;
const LONGITUDE_MAX = 180;

interface FormErrors {
  name?: string;
  description?: string;
  coordinates?: string;
}

const REPORT_PREFIX = '[REPORT:';

const SuggestEditForm: React.FC<SuggestEditFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mosque,
}) => {
  const { t } = useLanguage();
  const toast = useToast();
  const { isVisible, isClosing, handleClose } = useModalAnimation({ isOpen, onClose });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [reason, setReason] = useState('');
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [coordinatesInput, setCoordinatesInput] = useState('');
  const [isClosedOrNotHere, setIsClosedOrNotHere] = useState(false);
  const [submitterName, setSubmitterName] = useState('');
  const [formData, setFormData] = useState<MosqueFormData>({
    name: '',
    type: 'mosque',
    isPublic: true,
    facilities: {
      menWc: null,
      womenWc: null,
      menWudu: null,
      womenWudu: null,
      womenPrayerArea: null,
    },
    description: '',
  });

  // Pre-fill form with current mosque data
  useEffect(() => {
    if (mosque) {
      setFormData({
        name: mosque.name,
        type: mosque.type,
        isPublic: mosque.isPublic,
        location: {
          lat: mosque.location.lat,
          lng: mosque.location.lng,
        },
        facilities: { ...mosque.facilities },
        description: mosque.description || '',
      });
      setCoordinatesInput(`${mosque.location.lat}, ${mosque.location.lng}`);
      setReason('');
      setIsClosedOrNotHere(false);
      setSubmitterName('');
      setSubmitSuccess(false);
      setCountdown(COUNTDOWN_SECONDS);
      setErrors({});
      setTouched({});
    }
  }, [mosque]);

  // Validate a single field
  const validateField = (field: string, value: string): string | undefined => {
    switch (field) {
      case 'name':
        if (!value.trim()) {
          return t('validation.nameRequired');
        }
        if (value.trim().length < NAME_MIN_LENGTH) {
          return t('validation.nameTooShort');
        }
        if (value.length > NAME_MAX_LENGTH) {
          return t('validation.nameTooLong');
        }
        return undefined;
      case 'description':
        if (value.length > DESCRIPTION_MAX_LENGTH) {
          return t('validation.descriptionTooLong');
        }
        return undefined;
      case 'coordinates': {
        const parts = value.split(',').map(part => part.trim());
        if (parts.length !== 2) {
          return `${t('validation.latitudeInvalid')} / ${t('validation.longitudeInvalid')}`;
        }

        const latitude = Number(parts[0]);
        const longitude = Number(parts[1]);

        if (Number.isNaN(latitude)) {
          return t('validation.latitudeInvalid');
        }
        if (latitude < LATITUDE_MIN || latitude > LATITUDE_MAX) {
          return t('validation.latitudeRange');
        }
        if (Number.isNaN(longitude)) {
          return t('validation.longitudeInvalid');
        }
        if (longitude < LONGITUDE_MIN || longitude > LONGITUDE_MAX) {
          return t('validation.longitudeRange');
        }

        return undefined;
      }
      default:
        return undefined;
    }
  };

  // Validate all fields
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      name: validateField('name', formData.name),
      description: validateField('description', formData.description || ''),
      coordinates: validateField('coordinates', coordinatesInput),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== undefined);
  };

  // Handle field blur for validation
  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const value =
      field === 'name'
        ? formData.name
        : field === 'description'
          ? (formData.description || '')
          : coordinatesInput;
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Countdown timer for success modal
  useEffect(() => {
    if (submitSuccess && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (submitSuccess && countdown === 0) {
      handleClose();
      setSubmitSuccess(false);
    }
  }, [submitSuccess, countdown, handleClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mosque) return;

    // Mark all fields as touched
    setTouched({ name: true, description: true, coordinates: true });

    // Validate all fields
    if (!validateForm()) {
      return;
    }

    await submitSuggestion(false);
  };

  const submitSuggestion = async (closedOrNotHere: boolean) => {
    if (!mosque || isSubmitting) return;

    const [latitudeStr, longitudeStr] = coordinatesInput.split(',').map(part => part.trim());
    const latitude = Number(latitudeStr);
    const longitude = Number(longitudeStr);
    const hasValidCoordinates = !Number.isNaN(latitude) && !Number.isNaN(longitude);

    setIsSubmitting(true);
    try {
      await onSubmit(
        mosque.id,
        {
          ...formData,
          location: {
            lat: hasValidCoordinates ? latitude : mosque.location.lat,
            lng: hasValidCoordinates ? longitude : mosque.location.lng,
          },
        },
        buildSubmitReason(reason, closedOrNotHere),
        submitterName.trim() || undefined
      );
      setSubmitSuccess(true);
      setCountdown(COUNTDOWN_SECONDS);
    } catch (error) {
      console.error('Failed to submit suggestion:', error);
      toast.error(t('suggestEdit.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosedOrNotHereSubmit = async () => {
    if (!window.confirm(t('suggestEdit.confirmClosedOrNotHere'))) {
      return;
    }

    setIsClosedOrNotHere(true);
    await submitSuggestion(true);
  };

  const buildSubmitReason = (rawReason: string, closedOrNotHere: boolean) => {
    const normalizedReason = rawReason.trim();
    if (!closedOrNotHere) {
      return normalizedReason;
    }

    const reportPrefix = `${REPORT_PREFIX}closed_or_moved]`;
    if (normalizedReason) {
      return `${reportPrefix} ${normalizedReason}`;
    }
    return reportPrefix;
  };

  const handleFacilityChange = (facility: keyof MosqueFormData['facilities'], value: boolean | null) => {
    setFormData(prev => ({
      ...prev,
      facilities: {
        ...prev.facilities,
        [facility]: value,
      },
    }));
  };

  if (!isVisible || !mosque) return null;

  if (submitSuccess) {
    return (
      <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
        <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
          <div className="success-message">
            <CheckCircle size={48} className="success-icon" />
            <h2>{t('suggestEdit.thankYou')}</h2>
            <p>{t('suggestEdit.successMessage')}</p>
            <div className="countdown-indicator">
              <div className="countdown-bar" style={{ width: `${(countdown / COUNTDOWN_SECONDS) * 100}%` }} />
            </div>
            <p className="countdown-text">{t('common.closingIn')} {countdown}s</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('suggestEdit.title')}</h2>
          <button className="close-btn" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mosque-form">
          <div className="form-content">
            <p className="edit-info">
              {t('suggestEdit.editing')} <strong>{mosque.name}</strong>
            </p>

                <div className="form-group">
                  <label htmlFor="name">{t('addMosque.name')} *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, name: e.target.value }));
                      if (touched.name) {
                        setErrors(prev => ({ ...prev, name: validateField('name', e.target.value) }));
                      }
                    }}
                    onBlur={() => handleBlur('name')}
                    className={touched.name && errors.name ? 'input-error' : ''}
                    maxLength={NAME_MAX_LENGTH}
                  />
                  {touched.name && errors.name ? (
                    <span className="form-error">{errors.name}</span>
                  ) : (
                    <span className="form-hint">{t('addMosque.nameHint')}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="type">{t('addMosque.type')} *</label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                  >
                    <option value="mosque">{t('mosqueTypes.mosque')}</option>
                    <option value="prayer_room">{t('mosqueTypes.prayerRoom')}</option>
                    <option value="hired_hall">{t('mosqueTypes.hiredHall')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="description">{t('addMosque.description')}</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, description: e.target.value }));
                      if (touched.description) {
                        setErrors(prev => ({ ...prev, description: validateField('description', e.target.value) }));
                      }
                    }}
                    onBlur={() => handleBlur('description')}
                    className={touched.description && errors.description ? 'input-error' : ''}
                    placeholder={t('addMosque.descriptionPlaceholder')}
                    rows={3}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                  {touched.description && errors.description ? (
                    <span className="form-error">{errors.description}</span>
                  ) : (
                    <span className="form-hint">{formData.description?.length || 0}/{DESCRIPTION_MAX_LENGTH}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="coordinates">{t('suggestEdit.latitudeLabel')}/{t('suggestEdit.longitudeLabel')} *</label>
                  <input
                    type="text"
                    id="coordinates"
                    value={coordinatesInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCoordinatesInput(value);
                      if (touched.coordinates) {
                        setErrors(prev => ({ ...prev, coordinates: validateField('coordinates', value) }));
                      }
                    }}
                    onBlur={() => handleBlur('coordinates')}
                    className={touched.coordinates && errors.coordinates ? 'input-error' : ''}
                    placeholder="52.255295, 0.4149553"
                    inputMode="decimal"
                  />
                  {touched.coordinates && errors.coordinates ? (
                    <span className="form-error">{errors.coordinates}</span>
                  ) : (
                    <span className="form-hint">52.255295, 0.4149553</span>
                  )}
                </div>

                <div className="form-group">
                  <h3>{t('addMosque.access')}</h3>
                  <div className="access-toggle">
                    <div
                      className="access-slider"
                      style={{ transform: `translateX(${formData.isPublic ? 0 : 100}%)` }}
                    />
                    <button
                      type="button"
                      className={`facility-btn ${formData.isPublic ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, isPublic: true }))}
                    >
                      <Globe size={16} /> {t('mosqueDetails.public')}
                    </button>
                    <button
                      type="button"
                      className={`facility-btn ${!formData.isPublic ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, isPublic: false }))}
                    >
                      <Lock size={16} /> {t('mosqueDetails.private')}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <h3>{t('addMosque.facilities')}</h3>
                  <div className="facilities-segmented">
                    {(Object.keys(formData.facilities) as Array<keyof MosqueFormData['facilities']>).map((key) => (
                      <SegmentedControl
                        key={key}
                        label={t(`facilities.${key}`)}
                        value={formData.facilities[key]}
                        onChange={(value) => handleFacilityChange(key, value)}
                      />
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <button
                    type="button"
                    className={`checkbox-label checkbox-label--warning ${isClosedOrNotHere ? 'checked' : ''}`}
                    onClick={handleClosedOrNotHereSubmit}
                    disabled={isSubmitting}
                  >
                    <span className="closed-or-not-here-label">
                      <AlertTriangle size={14} aria-hidden="true" />
                      {t('suggestEdit.closedOrNotHere')}
                    </span>
                  </button>
                </div>

                <div className="form-group">
                  <label htmlFor="reason">{t('suggestEdit.reasonLabel')}</label>
                  <textarea
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t('suggestEdit.reasonPlaceholder')}
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="submitterName">{t('addMosque.submitterName')}</label>
                  <input
                    type="text"
                    id="submitterName"
                    value={submitterName}
                    onChange={(e) => setSubmitterName(e.target.value)}
                    placeholder={t('addMosque.submitterName')}
                    maxLength={80}
                  />
                  <span className="form-hint">{t('addMosque.submitterNameHint')}</span>
                </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleClose} className="btn-cancel" disabled={isSubmitting}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting ? <><ButtonSpinner /> {t('suggestEdit.submitting')}</> : t('suggestEdit.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuggestEditForm;
